import { BattleState, Monster } from '../types';

/**
 * Lv6 達人 AI のリソース最適化
 *
 * 特殊攻撃回数・リフレクター残数などの限られたリソースを、
 * 試合の進行度（HP残量比）に応じて動的に評価する。
 *
 * 例:
 * - 試合序盤（両者HP高）: 特殊は温存価値が高い（後で使う方が有利）
 * - 試合中盤（相手HP半分）: 使用推奨（とどめチャンス）
 * - 試合終盤（自分瀕死）: 出し惜しみ無用（使い切るべき）
 *
 * 純粋関数。同じ入力に対して常に同じ結果を返す。
 */

/**
 * リソース動的乗数（resourceOptimization=100 のときの最大変動幅）
 *
 * 序盤の温存ボーナスや終盤のディスカウントを表す係数。
 */
export const RESOURCE_MULTIPLIERS = {
  /** 相手瀕死時の特殊価値乗数（温存より使用優先） */
  OPP_LOW_HP: 0.5,
  /** 標準時 */
  STANDARD: 1.0,
  /** 序盤時（両者HP高） */
  EARLY_GAME: 1.5,
  /** 自分瀕死時（出し惜しみ無用） */
  OWN_LOW_HP: 0.3,
} as const;

/**
 * 特殊攻撃残数の動的価値乗数を計算
 *
 * @param ownHpRatio 自分のHP残量比 (0-1)
 * @param oppHpRatio 相手のHP残量比 (0-1)
 * @param strength 0-1 (resourceOptimization/100) この値が高いほど動的乗数の影響が強い
 * @returns 1.0 を中心とした乗数（resourceOptimization の strength で線形補間）
 */
export function getSpecialReserveMultiplier(
  ownHpRatio: number,
  oppHpRatio: number,
  strength: number,
): number {
  let baseMultiplier: number = RESOURCE_MULTIPLIERS.STANDARD;

  // 自分瀕死: 出し惜しみ無用（最優先）
  if (ownHpRatio < 0.2) {
    baseMultiplier = RESOURCE_MULTIPLIERS.OWN_LOW_HP;
  }
  // 相手瀕死: 使用推奨
  else if (oppHpRatio < 0.3) {
    baseMultiplier = RESOURCE_MULTIPLIERS.OPP_LOW_HP;
  }
  // 序盤（両者HP高）: 温存
  else if (ownHpRatio >= 0.7 && oppHpRatio >= 0.7) {
    baseMultiplier = RESOURCE_MULTIPLIERS.EARLY_GAME;
  }
  // それ以外（中盤）: 標準

  // strength=0 で 1.0、strength=1 で baseMultiplier
  return 1 + (baseMultiplier - 1) * strength;
}

/**
 * リフレクター残数の動的価値乗数を計算
 *
 * リフレクターは「相手が特殊攻撃を撃ってくる頻度」と「残ターン数」に影響する。
 * 簡略化のため、HP状況のみで補正（相手特殊残数は別軸で評価済み）。
 */
export function getReflectorReserveMultiplier(
  ownHpRatio: number,
  oppHpRatio: number,
  strength: number,
): number {
  let baseMultiplier: number = RESOURCE_MULTIPLIERS.STANDARD;

  // 自分瀕死: リフレクターは温存しても無意味（攻撃に使わない手で死ぬくらいなら攻撃すべき）
  if (ownHpRatio < 0.2) {
    baseMultiplier = RESOURCE_MULTIPLIERS.OWN_LOW_HP;
  }
  // 相手瀕死: 攻撃に集中（リフレクター価値ダウン）
  else if (oppHpRatio < 0.3) {
    baseMultiplier = RESOURCE_MULTIPLIERS.OPP_LOW_HP;
  }
  // 序盤: 温存価値高い
  else if (ownHpRatio >= 0.7 && oppHpRatio >= 0.7) {
    baseMultiplier = RESOURCE_MULTIPLIERS.EARLY_GAME;
  }

  return 1 + (baseMultiplier - 1) * strength;
}

/**
 * BattleState から HP 残量比のペアを抽出するヘルパー
 */
export function getHpRatios(
  state: BattleState,
  ownPlayerId: 'player1' | 'player2',
  ownMonster: Monster,
  opponentMonster: Monster,
): { ownHpRatio: number; oppHpRatio: number } {
  const opponentId: 'player1' | 'player2' = ownPlayerId === 'player1' ? 'player2' : 'player1';
  const ownHpRatio = state[ownPlayerId].currentHp / ownMonster.stats.hp;
  const oppHpRatio = state[opponentId].currentHp / opponentMonster.stats.hp;
  return { ownHpRatio, oppHpRatio };
}
